import {Component, computed, effect, inject, input, ViewEncapsulation} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, FormGroupDirective, Validators} from "@angular/forms";

import {HarbourDuesRotterdamResponse} from "@portbase/bezoekschip-service-typescriptmodels";
import {HarbourDuesIncidentalForm} from "../../../../types";

@Component({
  selector: 'app-harbour-dues-rotterdam-incidental',
  templateUrl: './harbour-dues-rotterdam-incidental.component.html',
  styleUrls: ['./harbour-dues-rotterdam-incidental.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class IncidentalComponent {
  readonly form = inject(FormGroupDirective);
  private readonly fb = inject(FormBuilder);
  readonly incidentals : FormArray<FormGroup<HarbourDuesIncidentalForm>> =
    this.form.control.get('incidentals') as FormArray<FormGroup<HarbourDuesIncidentalForm>>;
  readonly harbourDuesDetails = input<HarbourDuesRotterdamResponse>();
  readonly isHarbourDuesAdvisor = input(false);
  readonly canEditIncidental = computed(() => {
    return this.isHarbourDuesAdvisor(); // true
  })

  // TODO: required remark and hidden amount should come from BTP
  private readonly remarkRequiredTypes = ['2', '3'];
  private readonly amountHiddenTypes = ['5'];

  readonly incidentalTypes = computed(() =>
    this.harbourDuesDetails()?.lookupIncidentals.map(
      incidental => incidental.incidentalType
    ) ?? []
  )

  constructor() {
    this.initExistingIncidentals();

    effect(() => {
      if (this.canEditIncidental()) {
        this.incidentals.enable({ emitEvent: false });
      } else {
        this.incidentals.disable({ emitEvent: false });
      }
    });
  }

  private initExistingIncidentals(): void {
    this.incidentals.controls.forEach(group => {
      this.applyRemarkValidation(group);
      this.subscribeToIncidentalType(group);
    });
  }

  private applyRemarkValidation(group: FormGroup<HarbourDuesIncidentalForm>): void {
    const type = group.controls.incidentalType.value;
    const reasonControl = group.controls.incidentalReason;
    const amountControl = group.controls.incidentalAmount;

    if (this.remarkRequiredTypes.includes(type)) {
      reasonControl.setValidators(Validators.required);
      if (reasonControl.value === null) {
        reasonControl.setValue('');
      }
      reasonControl.updateValueAndValidity();
    }

    if (this.amountHiddenTypes.includes(type)) {
      amountControl.clearValidators();
      amountControl.setValue(null);
      amountControl.updateValueAndValidity();
    }
  }

  isAmountHidden(incidental: FormGroup<HarbourDuesIncidentalForm>): boolean {
    return this.amountHiddenTypes.includes(incidental.controls.incidentalType.value);
  }

  incidentalFormatter = (incidentalType: string) => {
    const lookupIncidental = this.harbourDuesDetails()?.lookupIncidentals.find(
      incidental => incidental.incidentalType === incidentalType
    );
    return `${lookupIncidental.incidentalType} - ${lookupIncidental.description}`
  }

  removeIncidental(index: number): void {
    this.incidentals.removeAt(index);
  }

  addIncidental(): void {
    const group = this.createIncidentalForm();
    this.subscribeToIncidentalType(group);
    this.incidentals.push(group);
  }

  private subscribeToIncidentalType(group: FormGroup<HarbourDuesIncidentalForm>): void {
    group.controls.incidentalType.valueChanges.subscribe(type => {
      const reasonControl = group.controls.incidentalReason;
      if (this.remarkRequiredTypes.includes(type)) {
        reasonControl.setValidators(Validators.required);
        if (reasonControl.value === null) {
          reasonControl.setValue('');
        }
      } else {
        reasonControl.clearValidators();
      }
      reasonControl.updateValueAndValidity();

      const amountControl = group.controls.incidentalAmount;
      if (this.amountHiddenTypes.includes(type)) {
        amountControl.clearValidators();
        amountControl.setValue(null);
      } else {
        amountControl.setValidators(Validators.required);
      }
      amountControl.updateValueAndValidity();
    });
  }

  createIncidentalForm(): FormGroup<HarbourDuesIncidentalForm> {
    return this.fb.group<HarbourDuesIncidentalForm>({
      incidentalType: this.fb.control(null),
      incidentalAmount: this.fb.control(null),
      incidentalReason: this.fb.control(null)
    });
  }

  initRemark(incidental : FormGroup<HarbourDuesIncidentalForm>) {
    incidental.controls.incidentalReason.setValue("");
  }
}
